import { useState, useRef, useCallback, useEffect, useId, type RefObject } from 'react';
import { isPrimaryMod } from '@/utils/input';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  mergeClasses,
} from '@coreai/fluentui-react';
import { ArrowUpRegular, SparkleRegular, DocumentBulletListRegular } from '@fluentui/react-icons';
import { ModelSelector } from '@/components/shared/ModelSelector';
import { useGitHubAuthStore } from '@/stores/githubAuthStore';
import { isFeatureEnabled } from '@/featureFlags';

const MIN_PROMPT_LENGTH = 10;

const SUGGESTIONS = [
  { label: 'Email → SharePoint', text: 'When a new email arrives, save the attachment to SharePoint' },
  { label: 'Service Bus → Cosmos DB', text: 'Process messages from a Service Bus queue and store results in Cosmos DB' },
  { label: 'Schedule → Teams alert', text: 'On a schedule, check an API endpoint and send a Teams notification if the status changes' },
];

// ── Styles ──────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  rootExpanded: {
    flexShrink: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: tokens.spacingVerticalM,
  },
  containerExpanded: {
    maxWidth: '600px',
    paddingTop: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalXXL,
  },
  containerCompact: {
    maxWidth: '100%',
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  titleExpanded: {
    margin: '0',
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  formExpanded: {
    gap: tokens.spacingVerticalS,
  },
  composer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '126px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    ':focus-within': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
  },
  textarea: {
    flex: 1,
    resize: 'none' as const,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    outline: 'none',
  },
  textareaStandalone: {
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  textareaExpanded: {
    minHeight: '74px',
    width: '100%',
    border: 'none',
    borderRadius: tokens.borderRadiusNone,
    backgroundColor: 'transparent',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    '&:disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  suggestionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  suggestionRowExpanded: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  suggestion: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  suggestionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  // Single footer row: model+auth left, actions right
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  footerExpanded: {
    flex: 1,
    padding: `0 ${tokens.spacingHorizontalS} ${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  footerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  githubLink: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    ':hover': {
      color: tokens.colorNeutralForeground2,
      textDecorationLine: 'underline',
    },
    whiteSpace: 'normal',
    height: 'auto',
  },
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  avatar: {
    width: '16px',
    height: '16px',
    borderRadius: tokens.borderRadiusCircular,
  },
  buildIconButton: {
    minWidth: '32px',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
});

// ── Footer Auth: inline model + GitHub auth ─────────────────────────────

type FooterStyles = ReturnType<typeof useStyles>;

const FooterAuth = ({ isCreating, styles }: { isCreating: boolean; styles: FooterStyles }) => {
  const { isAuthenticated, user, login } = useGitHubAuthStore();
  const isAssistantGitHubAndHistoryEnabled = isFeatureEnabled('assistantGitHubAndHistory');

  return (
    <div className={styles.footerLeft}>
      <ModelSelector disabled={isCreating} size="small" />
      {!isAssistantGitHubAndHistoryEnabled ? null : !isAuthenticated ? (
        <Button
          appearance="transparent"
          size="small"
          className={styles.githubLink}
          onClick={login}
        >
          Sign in with GitHub for more models
        </Button>
      ) : user ? (
        <div className={styles.avatarRow}>
          {user.avatarUrl && <img src={user.avatarUrl} alt="" className={styles.avatar} />}
          <Text size={200} className={styles.githubLink}>{user.login}</Text>
        </div>
      ) : null}
    </div>
  );
};

// ── Component ───────────────────────────────────────────────────────────

export type WorkflowBuilderHeroProps = {
  /** Dialogs supply their own title and surrounding chrome. */
  presentation?: 'page' | 'dialog';
  promptRef?: RefObject<HTMLTextAreaElement>;
  /** Whether workflows already exist (controls compact vs expanded layout) */
  hasWorkflows: boolean;
  /** Whether create mutation is in-flight */
  isCreating: boolean;
  /** Disabled state for the input */
  disabled?: boolean;
  /** Error from create mutation */
  error: string | null;
  /** Called with prompt text when user clicks Build */
  onBuild: (prompt: string) => void;
  /** Called when user clicks Build from templates (only provided when feature flag is on) */
  onBuildFromTemplate?: () => void;
};

export const WorkflowBuilderHero = ({
  presentation = 'page',
  promptRef,
  hasWorkflows,
  isCreating,
  disabled,
  error,
  onBuild,
  onBuildFromTemplate,
}: WorkflowBuilderHeroProps) => {
  const styles = useStyles();
  const [prompt, setPrompt] = useState('');
  const localTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = promptRef ?? localTextareaRef;
  const isComposingRef = useRef(false);
  const promptHintId = useId();
  const isDialog = presentation === 'dialog';
  const isExpanded = !isDialog && !hasWorkflows;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!hasWorkflows && textarea) {
      const { activeElement, body, documentElement } = textarea.ownerDocument;
      if (!activeElement || activeElement === body || activeElement === documentElement) {
        textarea.focus();
      }
    }
  }, [hasWorkflows, textareaRef]);

  const trimmedPrompt = prompt.trim();
  const hasValidPrompt = trimmedPrompt.length >= MIN_PROMPT_LENGTH;

  const handleBuild = useCallback(() => {
    if (!hasValidPrompt || isCreating || disabled) return;
    onBuild(trimmedPrompt);
  }, [disabled, hasValidPrompt, isCreating, onBuild, trimmedPrompt]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // ⌘+Enter on Mac, Ctrl+Enter on Win/Linux — the canonical
      // "submit a multi-line composer" chord (Slack, Discord, etc.).
      if (e.key === 'Enter' && isPrimaryMod(e) && !isComposingRef.current) {
        e.preventDefault();
        handleBuild();
      }
    },
    [handleBuild],
  );

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  }, []);

  const handleSuggestionClick = useCallback((text: string) => {
    setPrompt(text);
    textareaRef.current?.focus();
  }, [textareaRef]);

  return (
    <div
      className={mergeClasses(styles.root, isExpanded && styles.rootExpanded)}
      role="region"
      aria-label="Create a new workflow"
    >
      <div className={mergeClasses(styles.container, !isDialog && (hasWorkflows ? styles.containerCompact : styles.containerExpanded))}>
        <div className={styles.header}>
          {!isExpanded ? (
            <>
              {!isDialog && <Text className={styles.title}>Build a new workflow</Text>}
              <Text className={styles.subtitle}>
                {hasWorkflows
                  ? 'Describe your automation or pick an example below.'
                  : 'Describe what you want to automate and the AI assistant will build it, or start from a blank canvas.'}
              </Text>
            </>
          ) : (
            <Text as="h1" className={mergeClasses(styles.title, styles.titleExpanded)}>
              What would you like to automate today?
            </Text>
          )}
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={mergeClasses(styles.form, isExpanded && styles.formExpanded)}>
          {!isExpanded ? (
            <>
              <textarea
                ref={textareaRef}
                className={mergeClasses(styles.textarea, styles.textareaStandalone)}
                placeholder="Describe what your workflow should do..."
                aria-label="Describe your workflow for the assistant to build"
                aria-describedby={isDialog ? promptHintId : undefined}
                value={prompt}
                onChange={handlePromptChange}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => { isComposingRef.current = true; }}
                onCompositionEnd={() => { isComposingRef.current = false; }}
                rows={hasWorkflows ? 2 : 3}
                disabled={isCreating || disabled}
              />
              {isDialog && (
                <Text id={promptHintId} className={styles.subtitle}>
                  Enter at least {MIN_PROMPT_LENGTH} characters. You will name your workflow next.
                </Text>
              )}

              <div className={styles.suggestionRow}>
                <Text className={styles.suggestionLabel}>Examples</Text>
                {SUGGESTIONS.map((s) => (
                  <Button
                    key={s.label}
                    appearance="outline"
                    size="small"
                    className={styles.suggestion}
                    onClick={() => handleSuggestionClick(s.text)}
                    disabled={isCreating || !!disabled}
                    aria-label={`Example: ${s.label}`}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>

              <div className={styles.footer}>
                <FooterAuth isCreating={isCreating} styles={styles} />
                <div className={styles.footerRight}>
                  {onBuildFromTemplate && (
                    <Button
                      appearance="outline"
                      icon={<DocumentBulletListRegular />}
                      onClick={onBuildFromTemplate}
                      disabled={isCreating || !!disabled}
                    >
                      Build from templates
                    </Button>
                  )}
                  <Button
                    appearance="primary"
                    icon={isCreating ? <Spinner size="tiny" /> : <SparkleRegular />}
                    disabled={!hasValidPrompt || isCreating || !!disabled}
                    onClick={handleBuild}
                  >
                    {isCreating ? 'Building...' : 'Build'}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.composer} role="group" aria-label="Workflow builder">
                <textarea
                  ref={textareaRef}
                  className={mergeClasses(styles.textarea, styles.textareaExpanded)}
                  placeholder="Describe the workflow you want to automate and I'll build it..."
                  aria-label="Describe your workflow for the assistant to build"
                  value={prompt}
                  onChange={handlePromptChange}
                  onKeyDown={handleKeyDown}
                  onCompositionStart={() => { isComposingRef.current = true; }}
                  onCompositionEnd={() => { isComposingRef.current = false; }}
                  rows={3}
                  disabled={isCreating || disabled}
                />
                <div className={mergeClasses(styles.footer, styles.footerExpanded)}>
                  <FooterAuth isCreating={isCreating} styles={styles} />
                  <div className={styles.footerRight}>
                    {onBuildFromTemplate && (
                      <Button
                        appearance="outline"
                        icon={<DocumentBulletListRegular />}
                        onClick={onBuildFromTemplate}
                        disabled={isCreating || !!disabled}
                      >
                        Build from templates
                      </Button>
                    )}
                    <Button
                      appearance="primary"
                      className={styles.buildIconButton}
                      icon={isCreating ? <Spinner size="tiny" /> : <ArrowUpRegular />}
                      aria-label="Build"
                      disabled={!hasValidPrompt || isCreating || !!disabled}
                      onClick={handleBuild}
                    />
                  </div>
                </div>
              </div>

              <div
                className={mergeClasses(styles.suggestionRow, styles.suggestionRowExpanded)}
                role="group"
                aria-label="Examples"
              >
                <Text className={styles.suggestionLabel}>Examples</Text>
                {SUGGESTIONS.map((s) => (
                  <Button
                    key={s.label}
                    appearance="outline"
                    size="small"
                    className={styles.suggestion}
                    onClick={() => handleSuggestionClick(s.text)}
                    disabled={isCreating || !!disabled}
                    aria-label={`Example: ${s.label}`}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
